import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import H4 from '@base/H4/H4';
import H7 from '@base/H7/H7';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import CopyToClipboard from '@icon/CopyToClipboard';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import Image from '@base/Image';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background: linear-gradient(
      0deg,
      var(--color-base-light-0),
      var(--color-base-light-0)
    ),
    var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_8B};
  margin-top: ${Spacings.SPACING_6B};
`;

const InfoWrapper = styled.div`
  max-width: 244px;
`;

const StyH4 = styled(H4)`
  margin-bottom: ${Spacings.SPACING_1B};
`;

const LinkWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_5B};
  background: var(--color-base-1);
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  box-sizing: border-box;
  border-radius: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_6B};
  align-items: center;
  position: relative;
  cursor: pointer;
  width: 100%;
  overflow: hidden;
`;

const Link = styled(H7)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-decoration-line: underline;
  max-width: ${Spacings.SPACING_40B};
  margin-left: ${Spacings.SPACING_4B};
`;

const HoverButton = styled.div`
  background: var(--color-base-1);
  position: absolute;
  left: 0;
  top: ${({ $top }) => $top};
  transition: top 300ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyImage = styled.div`
  height: ${Spacings.SPACING_22B};
  width: ${Spacings.SPACING_22B};
`;

const hoverButtonText = {
  copyCode: 'Copy code',
  copied: 'Copied'
};

const GroupsInviteCard = ({ inviteLink }) => {
  const timer = useRef(null);
  const [buttonText, setButtonText] = useState(hoverButtonText.copyCode);
  const [hoverButtonTop, setHoverButtonTop] = useState('100%');
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);

  const onMouseEnter = () => {
    if (!timer.current) {
      setButtonText(hoverButtonText.copyCode);
      setHoverButtonTop('0%');
    }
  };
  const onMouseLeave = () => {
    if (!timer.current) {
      setHoverButtonTop('100%');
    }
  };

  const copyToClipboard = () =>
    navigator.clipboard.writeText(inviteLink).then(() => {
      setButtonText(hoverButtonText.copied);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        onMouseLeave();
      }, 2000);
    });

  return (
    <Wrapper>
      <InfoWrapper>
        <StyH4>Invite your friends</StyH4>
        <P1 color="var(--color-text-secondary)">
          Bring a friend to your group. Start learning together, compete with
          them in weekly group competitions.
        </P1>
        <LinkWrapper
          onClick={copyToClipboard}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <CopyToClipboard size={16} color="var(--color-text-primary)" />
          <Link
            color={
              isDarkModeOn ? 'var(--color-text-primary)' : 'var(--color-i-blue)'
            }
          >
            {inviteLink}
          </Link>
          <HoverButton
            $top={hoverButtonTop}
            $display={hoverButtonTop === '100%' ? 'none' : 'block'}
          >
            <H6
              color={
                buttonText === hoverButtonText.copyCode
                  ? 'var(--color-i-green)'
                  : 'var(--color-text-primary)'
              }
            >
              {buttonText}
            </H6>
          </HoverButton>
        </LinkWrapper>
      </InfoWrapper>
      <StyImage>
        <Image
          alt="unacademy"
          src="groups/invite.png"
          height={Spacings.SPACING_22B}
          width={Spacings.SPACING_22B}
        />
      </StyImage>
    </Wrapper>
  );
};

export default GroupsInviteCard;

GroupsInviteCard.propTypes = {
  inviteLink: PropTypes.string.isRequired
};
